import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  changeKey,
  createVersion,
  decideChange,
  getChange,
  getLanguages,
  getMe,
  LandingPagesError,
  languagesKey,
  meKey,
  recoverPage,
  replaceAsset,
  retirePage,
  writeMessage,
  type Approval,
  type ChangeData,
  type ChangePreview,
  type PageAsset,
  type PageSummary,
} from "./api";
import { approvedStatus, approveWording, changeLine, effectLine, languageName, type ApprovedState } from "./format";

// R6 write path (transparency specs/2026-09-25-landing-r6-write-path.md, UX flows F5, F7, F8).
// Each write is a commit on the page's pending PR; nothing is live until an approver approves.
// The Worker enforces every rule; these controls only hide what a person cannot do.

export function useMe() {
  return useQuery({ queryKey: meKey, queryFn: getMe, staleTime: 5 * 60_000 }).data?.data ?? null;
}

export function useRefreshLanding() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["landing_pages"] });
}

type Option = { value: string; label: string; disabled?: boolean };

/** One labelled dropdown (shell Select primitive); the dialogs below are built from these. */
function Choice({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** A page a new version can be copied from: deployed, not retired, no change waiting on it. */
function startable(pages: PageSummary[]): PageSummary[] {
  return pages.filter((p) => p.status !== "retired" && !p.pending);
}

function sourceOption(page: PageSummary, withLanguage: boolean): Option {
  const version = `Version ${page.variant.toUpperCase()}`;
  return { value: page.folder, label: withLanguage ? `${languageName(page.locale)} · ${version}` : `${version} · ${page.folder}` };
}

function useCreate(funnelSlug: string, onCreated: (folder: string) => void, close: () => void) {
  const refresh = useRefreshLanding();
  return useMutation({
    mutationFn: (input: { locale: string; start_from: string }) => createVersion({ funnel: funnelSlug, ...input }),
    onSuccess: (result) => {
      void refresh();
      close();
      onCreated(result.data.folder);
    },
  });
}

/** R6b: the first page in a language the shop sells in but this funnel has no page for yet. */
export function NewLanguageButton({
  funnelSlug,
  pages,
  onCreated,
}: {
  funnelSlug: string;
  pages: PageSummary[];
  onCreated: (folder: string) => void;
}) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const languages = useQuery({
    queryKey: languagesKey(funnelSlug),
    queryFn: () => getLanguages(funnelSlug),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const choices = languages.data?.data.new_languages ?? [];
  const [locale, setLocale] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const chosen = choices.find((c) => c.code === locale) ?? choices.find((c) => c.routable) ?? null;
  const sources = startable(pages).sort(
    (a, b) => Number(b.live) - Number(a.live) || Number(b.locale === "en") - Number(a.locale === "en") || a.folder.localeCompare(b.folder),
  );
  const chosenSource = source && sources.some((p) => p.folder === source) ? source : sources[0]?.folder ?? "";
  const create = useCreate(funnelSlug, onCreated, () => setOpen(false));
  const blocked = choices.filter((c) => !c.routable);

  if (!me?.can_edit || sources.length === 0) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>+ New language</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New language</DialogTitle>
            <DialogDescription>
              A first page in a language the shop already sells in. It starts as a copy of the page you pick, so
              translate its copy before approving. Nothing is live until Damien approves it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {languages.isPending && <p className="text-sm text-muted-foreground">Reading the shop's live languages…</p>}
            {languages.isError && (
              <p className="text-sm text-ks-ink" role="alert">
                The shop's languages could not be read just now. Try again shortly.
              </p>
            )}
            {languages.isSuccess && choices.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This funnel already has a page in every language the shop sells in.
              </p>
            )}
            {choices.length > 0 && (
              <>
                <Choice
                  id="language-new"
                  label="Language"
                  value={chosen?.code ?? ""}
                  options={choices.map((c) => ({
                    value: c.code,
                    label: c.routable ? languageName(c.code) : `${languageName(c.code)} · needs engineering`,
                    disabled: !c.routable,
                  }))}
                  onChange={setLocale}
                />
                <Choice
                  id="language-source"
                  label="Start from"
                  value={chosenSource}
                  options={sources.map((page) => sourceOption(page, true))}
                  onChange={setSource}
                />
                {chosen && (
                  <p className="text-sm text-muted-foreground">
                    This page will live at /{chosen.code}/{funnelSlug} once approved.
                  </p>
                )}
                {blocked.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {blocked.length === 1 ? "One language is" : `${blocked.length} languages are`} marked "needs
                    engineering": the shop sells in {blocked.length === 1 ? "it" : "them"}, but the site has no page
                    address in {blocked.length === 1 ? "that language" : "those languages"} yet. Ask Damien to add it.
                  </p>
                )}
              </>
            )}
            {create.isError && <p className="text-sm text-ks-ink" role="alert">{writeMessage(create.error)}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              disabled={!chosen || !chosenSource || create.isPending}
              onClick={() => chosen && create.mutate({ locale: chosen.code, start_from: chosenSource })}
            >
              {create.isPending ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** R6c: open the draft on its private preview site before approving it. */
export function PreviewLink({ preview }: { preview: ChangePreview | null }) {
  if (!preview) return null;
  if (preview.status === "ready" && preview.url) {
    const url = preview.url;
    return (
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => window.open(url, "_blank", "noopener")}>
        Preview
      </Button>
    );
  }
  if (preview.status === "building") {
    return <span className="text-sm">Preparing the preview. The Preview button appears here in about 3 minutes.</span>;
  }
  return (
    <span className="text-sm">
      The preview could not be built.{" "}
      {preview.run_url && (
        <a href={preview.run_url} target="_blank" rel="noreferrer" className="underline">Build log</a>
      )}
    </span>
  );
}

/** After an approved change is merged: every landing query reloads except the panel's own tracker. */
export function reloadsAfterDeploy(queryKey: readonly unknown[], folder: string, mergeSha: string | null, pr?: number | null): boolean {
  const tracking = changeKey(folder, mergeSha ?? undefined, pr ?? undefined);
  return queryKey[0] === "landing_pages" && JSON.stringify(queryKey) !== JSON.stringify(tracking);
}

export type ApprovalView = { state: ApprovedState; release: string | null; url: string | null; rolledBack?: boolean; productionCheckFailed?: boolean };

type ApprovalFlags = Pick<Approval, "checks" | "auto_merge" | "server_finish" | "finish_blocked">;

/**
 * Why the console finisher will not complete this approval, or null when it will (or the Worker is older and says
 * nothing). A Worker that sends no reason is read by the only rule it has: the re-run bound applies to a failed check
 * alone, so server_finish false with checks not failed is a foreign commit.
 */
export function finishBlocked(approval: ApprovalFlags | null | undefined): "foreign_commit" | "retry_used" | null {
  if (!approval || approval.server_finish !== false) return null;
  if (approval.finish_blocked) return approval.finish_blocked;
  return approval.checks === "failure" ? "retry_used" : "foreign_commit";
}

/** What an approval covers, for the panel past the merge: a retire reads as a retire to "Retired", never as a publish. */
export function approvedFrom(data: ChangeData | undefined): { kind: string; variant: string | null } {
  const effect = data?.effect ?? null;
  // Retire names the version leaving; recover (and a new version) names the one arriving.
  const moved = !effect
    ? []
    : effect.kind === "retire"
      ? effect.live_before.filter((v) => !effect.live_after.includes(v))
      : effect.live_after.filter((v) => !effect.live_before.includes(v));
  const action = data?.pending?.changes.find((c) => c.action === "retire" || c.action === "recover")?.action;
  return { kind: effect?.kind ?? action ?? "new_version", variant: moved[0] ?? null };
}

/**
 * The PR an approved change must be followed by. A tab opened after the approval never pressed approve itself, so
 * without this it would lose the change at the finisher's merge (the plain read has no pending and no deploy then).
 */
export function trackPrFor(data: ChangeData | undefined): number | null {
  return data?.pending?.approval ? data.pending.pr.number : null;
}

/** The panel's onError, as a pure step: whether the approve only lost to the finisher, and which PR to follow then. */
export function afterDecideError(
  error: unknown,
  decision: "approve" | "reject",
  pending: { pr: { number: number }; approval?: ApprovalFlags | null } | null | undefined,
): { raced: boolean; trackPr: number | null } {
  if (!lostFinishRace(error, decision, pending?.approval)) return { raced: false, trackPr: null };
  return { raced: true, trackPr: pending?.pr.number ?? null };
}

/** A query refetchInterval that knows how long the current approved state has shown (changePollMs). */
export function useChangePoll(trackingPr: number | null) {
  const since = useRef<{ state: ApprovedState | null; at: number }>({ state: null, at: 0 });
  return (data: ChangeData | undefined): number | false => {
    const state = approvalView(data)?.state ?? null;
    if (since.current.state !== state) since.current = { state, at: Date.now() };
    return changePollMs(data, trackingPr, Date.now() - since.current.at);
  };
}

/** Which approved state the panel shows, read from the change; null = not approved yet. */
export function approvalView(data: ChangeData | undefined): ApprovalView | null {
  const deploy = data?.deploy;
  if (deploy) {
    if (deploy.status !== "completed") return { state: "deploying", release: null, url: deploy.url };
    if (deploy.conclusion !== "success") return { state: "failed", release: null, url: deploy.url };
    // The release run only puts it on staging; visitors see it once the release is promoted.
    // An older Worker sends no release and cannot tell the two apart, so it keeps the old "done".
    if (deploy.release && !deploy.release.live) {
      const { release_id: release, state, promotion } = deploy.release;
      // Bug 3fd0402f rec 3: the promotion runs by itself. A run in flight wins (a person may have re-run a failed one);
      // a finished failed one, or a rolled-back release, needs a person and says so.
      if (promotion && promotion.status !== "completed") return { state: "promoting", release, url: promotion.url };
      if (state === "rolled_back" || (promotion?.status === "completed" && promotion.conclusion !== "success")) {
        return { state: "promote_failed", release, url: promotion?.url ?? null, rolledBack: state === "rolled_back" };
      }
      // Ledger `failed` after a promotion that completed: the production check failed afterwards (review round 4).
      if (state === "failed" && promotion?.status === "completed") {
        return { state: "promote_failed", release, url: promotion.url, productionCheckFailed: true };
      }
      // Ledger `failed` with no promotion: a release build that failed its checks, so the site did not update.
      if (state === "failed") return { state: "failed", release, url: deploy.url };
      return { state: "staging", release, url: null };
    }
    return { state: "done", release: deploy.release?.release_id ?? null, url: null };
  }
  const approval = data?.pending?.approval;
  if (approval) return { state: approval.checks === "failure" ? "checks_failed" : "checking", release: null, url: approval.checks_url };
  return null;
}

/** Which reload an approved state calls for: "merged" once main has it (every after-merge state), "live" once served. */
export function settledAs(state: ApprovedState | undefined): "live" | "merged" | null {
  if (state === "done") return "live";
  if (state === "staging" || state === "failed" || state === "promoting" || state === "promote_failed") return "merged";
  return null;
}

/**
 * Whether this open page must finish the publish itself: the checks passed and nothing server-side will merge it (no
 * GitHub auto-merge, and an older Worker with no console finisher). With the finisher the page only watches, so its
 * own approve never races the finisher's merge (review round 2 of os-portal PR 648).
 */
export function finishesInTab(approval: ApprovalFlags | null | undefined): boolean {
  // Never when the merge is known to be refused (a foreign commit): the Worker would answer merge_blocked.
  return approval?.checks === "success" && !approval.auto_merge && approval.server_finish !== true && finishBlocked(approval) !== "foreign_commit";
}

/** How long a failed promotion is still re-read: long enough for a person to re-run it, never for ever. */
export const PROMOTE_FAILED_POLL_FOR_MS = 30 * 60_000;

/**
 * A failed approve that only means the console finisher got there first: it merged the change (`no_change`). Only when
 * the Worker said it finishes server-side. A `stale_change` stays an error: it is as likely a teammate's new commit,
 * which the approver must see (review round 4).
 */
export function lostFinishRace(error: unknown, decision: "approve" | "reject", approval: Pick<Approval, "server_finish"> | null | undefined): boolean {
  if (decision !== "approve" || approval?.server_finish !== true) return false;
  if (!(error instanceof LandingPagesError) || error.body === null || typeof error.body !== "object") return false;
  return (error.body as { error?: unknown }).error === "no_change";
}

/** Each slow state is re-read for a while only: long enough for its run to finish or be re-run, never for ever. */
export const STAGING_POLL_FOR_MS = 60 * 60_000;
/** The finisher re-stamps failed checks as soon as CI completes, so its re-run shows well inside this. */
export const CHECKS_FAILED_POLL_FOR_MS = 30 * 60_000;

/**
 * How often the panel re-reads the change: fast while something is moving, never when it is settled.
 * `stateForMs`: how long the panel has shown the current state.
 */
export function changePollMs(data: ChangeData | undefined, trackingPr: number | null, stateForMs = 0): number | false {
  const view = approvalView(data);
  if (view?.state === "checking" || view?.state === "deploying") return 10_000;
  if (view?.state === "promoting") return 30_000;
  // Waiting for a promotion that may never come (a newer release went live, or was rolled back): an hour, then stop.
  if (view?.state === "staging") return stateForMs < STAGING_POLL_FOR_MS ? 30_000 : false;
  // Slow, and for 30 minutes only: a person re-running the promotion turns it back into "going live" without a reload.
  if (view?.state === "promote_failed") return stateForMs < PROMOTE_FAILED_POLL_FOR_MS ? 60_000 : false;
  // Failed checks the console finisher still owns (it re-runs them once by itself, and Try again is hidden): keep
  // watching for that re-run, its merge or retry_used, for 30 minutes (review round 5). Otherwise a person acts.
  if (view?.state === "checks_failed") {
    return data?.pending?.approval?.server_finish === true && stateForMs < CHECKS_FAILED_POLL_FOR_MS ? 30_000 : false;
  }
  if (trackingPr && !data?.pending && !data?.deploy) return 5_000;
  return data?.pending?.preview?.status === "building" ? 15_000 : false;
}

/**
 * The panel of an approved change on its way to live. `decide.raced`: the last approve lost to the console finisher
 * (lostFinishRace), which is no error: no error line, no Try again.
 */
export function ApprovedAlert({
  view,
  approval,
  kind,
  variant,
  canRetry,
  decide,
  onRetry,
}: {
  view: ApprovalView;
  approval: ApprovalFlags | null;
  kind: string;
  variant: string | null;
  canRetry: boolean;
  decide: { isPending: boolean; error: unknown; raced: boolean };
  onRetry: () => void;
}) {
  const name = variant ? `Version ${variant.toUpperCase()}` : "This version";
  const blocked = finishBlocked(approval);
  const { title, line } = approvedStatus(kind, view.state, name, view.release, {
    serverFinish: approval?.server_finish,
    blocked,
    autoMerge: approval?.auto_merge === true,
    rolledBack: view.rolledBack === true,
    productionCheckFailed: view.productionCheckFailed === true,
  });
  const failedDecide = decide.error !== null && !decide.raced;
  // A visible way on whenever a person is the next step: a failed check the finisher will not re-run (or no finisher),
  // a failed finish, or checks passed with nothing server-side to merge it. Never for a foreign commit (refused anyway),
  // and never while the finisher still owns the re-run (a press would use it up).
  const checksStuck = view.state === "checks_failed" && approval?.server_finish !== true;
  const stuck = blocked !== "foreign_commit" && (checksStuck || failedDecide || finishesInTab(approval));
  const retry = stuck && canRetry;
  return (
    <Alert aria-live="polite">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {line}{" "}
        {view.url && (view.state === "failed" || view.state === "checks_failed" || view.state === "checking" || view.state === "promoting" || view.state === "promote_failed") && (
          <a href={view.url} target="_blank" rel="noreferrer" className="underline">
            {view.state === "failed" ? "Deploy log" : view.state === "promoting" || view.state === "promote_failed" ? "Promote log" : "Check log"}
          </a>
        )}
        {retry && (
          <div className="mt-2">
            <Button size="sm" disabled={decide.isPending} onClick={onRetry}>
              {decide.isPending ? "Working…" : view.state === "checks_failed" || failedDecide ? "Try again" : "Publish now"}
            </Button>
          </div>
        )}
        {failedDecide && <p className="mt-2 text-sm" role="alert">{writeMessage(decide.error)}</p>}
      </AlertDescription>
    </Alert>
  );
}

/** The pending change on one page: what, who, and Approve / Reject for an approver, then its way to live. */
export function PendingPanel({ folder }: { folder: string }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [mergeSha, setMergeSha] = useState<string | null>(null);
  // An approve waiting for its checks: follow the PR until GitHub merges it.
  const [trackingPr, setTrackingPr] = useState<number | null>(null);
  // What was approved, kept past the merge (the change query then has no pending effect):
  // a retire must read as a retire all the way to "Retired" (a recover to "Recovered"), never as a publish.
  const [approved, setApproved] = useState<{ kind: string; variant: string | null } | null>(null);
  const [confirming, setConfirming] = useState<"approve" | "reject" | null>(null);
  const finishing = useRef<string | null>(null);
  // An approve that lost to the console finisher (it merged first): the change moved on, so no error is shown.
  const [raced, setRaced] = useState(false);
  const poll = useChangePoll(trackingPr);
  const change = useQuery({
    queryKey: changeKey(folder, mergeSha ?? undefined, trackingPr ?? undefined),
    queryFn: () => getChange(folder, mergeSha ?? undefined, trackingPr ?? undefined),
    refetchInterval: (query) => poll(query.state.data?.data),
    // Switching to the tracked read keeps the last answer on screen, never an empty panel.
    placeholderData: (previous) => previous,
  });
  const decide = useMutation({
    mutationFn: (input: { decision: "approve" | "reject"; head: string }) => decideChange(folder, input.decision, input.head),
    onMutate: () => setRaced(false),
    onSuccess: (result, { decision }) => {
      setConfirming(null);
      if (decision !== "approve") {
        void refresh();
        return;
      }
      if (!approved) setApproved(approvedFrom(change.data?.data));
      // Approve: the page reloads once the merge is through (effect below). Reloading now would
      // read a site that does not have the new version yet.
      if (result.data.merge_sha) setMergeSha(result.data.merge_sha);
      else {
        const prNumber = result.data.pr?.number ?? change.data?.data.pending?.pr.number;
        if (prNumber) setTrackingPr(prNumber);
        void change.refetch();
      }
    },
    onError: (error, { decision }) => {
      // The finisher merged it under us: follow the PR to its merge instead of showing an error.
      const outcome = afterDecideError(error, decision, change.data?.data.pending);
      if (outcome.raced) {
        setRaced(true);
        if (!approved) setApproved(approvedFrom(change.data?.data));
        if (outcome.trackPr) setTrackingPr(outcome.trackPr);
      }
      void change.refetch();
    },
  });

  const data = change.data?.data;
  const view = approvalView(data);
  const pending = data?.pending ?? null;
  const approval = pending?.approval ?? null;
  // An approval this tab did not make (opened afterwards, or reloaded): follow its PR through the finisher's merge,
  // and keep what it covers, exactly as after an approve made here.
  const followPr = trackPrFor(data);
  useEffect(() => {
    if (followPr === null || trackingPr !== null || mergeSha !== null) return;
    if (!approved) setApproved(approvedFrom(data));
    setTrackingPr(followPr);
  }, [followPr, trackingPr, mergeSha, approved, data]);
  // Finish the publish once per head, only when nothing server-side will (finishesInTab).
  useEffect(() => {
    if (!me?.can_approve || !pending || !finishesInTab(approval)) return;
    if (decide.isPending || finishing.current === pending.head_sha) return;
    finishing.current = pending.head_sha;
    decide.mutate({ decision: "approve", head: pending.head_sha });
  }, [me?.can_approve, pending, approval, decide]);

  // Reload once on the merge and once more when it goes live (the page and funnel views change then).
  const settled = settledAs(view?.state);
  const client = useQueryClient();
  useEffect(() => {
    // Reload everything except this panel's own tracking query. The base change entry (no merge
    // sha) is reloaded too: it still holds the merged change as pending, and a view that remounts
    // on it (an unreadable draft becoming readable) would offer Approve again.
    if (settled) {
      void client.invalidateQueries({ predicate: (query) => reloadsAfterDeploy(query.queryKey, folder, mergeSha, trackingPr) });
    }
  }, [settled, client, folder, mergeSha, trackingPr]);

  const errorLine = decide.isError && !raced ? <p className="mt-2 text-sm" role="alert">{writeMessage(decide.error)}</p> : null;
  if (view) {
    return (
      <ApprovedAlert
        view={view}
        approval={approval}
        kind={approved?.kind ?? data?.effect?.kind ?? "new_version"}
        variant={approved?.variant ?? null}
        canRetry={Boolean(me?.can_approve && pending)}
        decide={{ isPending: decide.isPending, error: decide.isError ? decide.error : null, raced }}
        onRetry={() => pending && decide.mutate({ decision: "approve", head: pending.head_sha })}
      />
    );
  }
  if (!pending) return null;

  const imageChanges = pending.changes.filter((c) => c.action === "replace_asset").length;
  // An older Worker may send no effect; the pending change's own action still names a retire or recover.
  const kind = data?.effect?.kind ?? pending.changes.find((c) => c.action === "retire" || c.action === "recover")?.action ?? null;
  const wording = approveWording(kind);
  return (
    <Alert>
      <AlertTitle>◐ Awaiting Damien · PR #{pending.pr.number}</AlertTitle>
      <AlertDescription>
        <ul className="my-2 flex flex-col gap-1">
          {pending.changes.map((c) => (
            <li key={`${c.at}-${c.action}`}>{changeLine(c)}</li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <PreviewLink preview={pending.preview ?? null} />
          {me?.can_approve && (
            <>
              <Button size="sm" onClick={() => { decide.reset(); setConfirming("approve"); }}>Approve</Button>
              <Button size="sm" variant="outline" className="text-foreground" onClick={() => { decide.reset(); setConfirming("reject"); }}>Reject</Button>
            </>
          )}
        </div>
        {errorLine}
      </AlertDescription>
      <Dialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirming === "approve" ? wording.title : "Reject this change?"}
            </DialogTitle>
            <DialogDescription>
              {confirming === "approve"
                ? data?.effect
                  ? effectLine(data.effect, imageChanges)
                  : wording.fallback
                : "Rejecting discards every pending change on this page. The live page stays as it is."}
            </DialogDescription>
          </DialogHeader>
          {/* The error shows inside the dialog too: behind it, a failed approve looked like a hang. */}
          {errorLine}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              variant={confirming === "reject" ? "destructive" : "default"}
              disabled={decide.isPending}
              onClick={() => confirming && decide.mutate({ decision: confirming, head: pending.head_sha })}
            >
              {decide.isPending
                ? "Working…"
                : decide.isError
                  ? "Try again"
                  : confirming === "approve"
                    ? wording.button
                    : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Alert>
  );
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

const MAX_BYTES = 2 * 1024 * 1024;

/** F8: replace one image; the new file and its alt text become a pending change. */
export function ReplaceAssetButton({ folder, asset }: { folder: string; asset: PageAsset }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState(asset.alt);
  const [problem, setProblem] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async () => {
      const bytes = new Uint8Array(await file!.arrayBuffer());
      return replaceAsset(folder, asset.slot, { filename: file!.name, alt, data: toBase64(bytes) });
    },
    onSuccess: () => {
      void refresh();
      setOpen(false);
      setFile(null);
      setProblem(null);
    },
  });

  if (!me?.can_edit) return null;
  // Every close starts the next open clean: no file, the current alt text, no stale error.
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setFile(null);
      setAlt(asset.alt);
      setProblem(null);
      upload.reset();
    }
  };
  const pick = (next: File | null) => {
    setProblem(null);
    if (next && !/\.(png|jpe?g)$/i.test(next.name)) setProblem("That file is not a PNG or JPG.");
    else if (next && next.size > MAX_BYTES) {
      setProblem(`That file is ${(next.size / 1024 / 1024).toFixed(1)} MB. Keep images under 2 MB, PNG or JPG.`);
    }
    setFile(next);
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Replace</Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace {asset.label}</DialogTitle>
            <DialogDescription>The new image waits for Damien's approval; the page keeps the current one until then.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Label htmlFor={`file-${asset.slot}`}>Image (PNG or JPG, under 2 MB)</Label>
            <Input
              id={`file-${asset.slot}`}
              type="file"
              accept="image/png,image/jpeg"
              onChange={(event) => pick(event.target.files?.[0] ?? null)}
            />
            <Label htmlFor={`alt-${asset.slot}`}>Alt text (leave empty if decorative)</Label>
            <Input id={`alt-${asset.slot}`} value={alt} onChange={(event) => setAlt(event.target.value)} />
            {(problem || upload.isError) && (
              <p className="text-sm text-ks-ink" role="alert">{problem ?? writeMessage(upload.error)}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
            <Button disabled={!file || problem !== null || upload.isPending} onClick={() => upload.mutate()}>
              {upload.isPending ? "Saving…" : "Save as pending change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** F7: retire a live version with a typed confirm; refused while it is the language's only live version. */
export function RetireButton({ folder, pagePath, onlyLive }: { folder: string; pagePath: string; onlyLive: boolean }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const retire = useMutation({
    mutationFn: () => retirePage(folder),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      setTyped("");
    },
  });

  if (!me?.can_edit) return null;
  if (onlyLive) {
    return (
      <p className="text-sm text-muted-foreground">
        This is the only live version of {pagePath}. Retiring it would take the page down.
      </p>
    );
  }
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>Retire this version…</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire {folder}?</DialogTitle>
            <DialogDescription>
              Its visitors go to the other live versions and its leads stay in Drip. This needs Damien's approval.
              Type the page name to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input value={typed} onChange={(event) => setTyped(event.target.value)} aria-label="Page name" placeholder={folder} />
          {retire.isError && <p className="text-sm text-ks-ink" role="alert">{writeMessage(retire.error)}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={typed !== folder || retire.isPending} onClick={() => retire.mutate()}>
              {retire.isPending ? "Saving…" : "Retire (pending approval)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Recover: put a retired version back into the split test, with a typed confirm. The mirror of Retire. */
export function RecoverButton({ folder }: { folder: string }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const recover = useMutation({
    mutationFn: () => recoverPage(folder),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      setTyped("");
    },
  });

  if (!me?.can_edit) return null;
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTyped("");
      recover.reset();
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>Recover this version…</Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recover {folder}?</DialogTitle>
            <DialogDescription>
              Recovering puts this version back into the split test once Damien approves it. It then gets a share of
              the visitors again. Type the page name to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input value={typed} onChange={(event) => setTyped(event.target.value)} aria-label="Page name" placeholder={folder} />
          {recover.isError && <p className="text-sm text-ks-ink" role="alert">{writeMessage(recover.error)}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
            <Button disabled={typed !== folder || recover.isPending} onClick={() => recover.mutate()}>
              {recover.isPending ? "Saving…" : "Recover (pending approval)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
